import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import {
  resolveActiveCompanyFilter,
  resolveToolbarTabCompanyFilter,
} from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import MainListModule from '@/__core/layout/module/MainListModule';
import {
  isTabVisibleForToolbarCompany,
  useModuleToolbarPermissions,
} from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';

import { createDefaultReportForm, reportStatusOptions, reportTypeOptions } from '../reportsMockData';
import { InventoryReportFormData, InventoryReportRow } from '../types';
import ReportForm from './ReportForm';
import ReportList from './ReportList';
import ToolbarListReport from './ToolbarListReport';

const hasGlobalRole = (globalRoles: any[]) =>
  globalRoles.some(
    (role) =>
      role?.role_level === 'global' || role?.name === 'global' || role?.role_name === 'global'
  );

const buildFormMetadata = (
  formData: InventoryReportFormData,
  reportId?: string,
  companyId?: string
) => ({
  formData,
  reportId,
  companyId: companyId || formData.company_id || undefined,
});

export default function ReportsModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const companies = useSelector((state: RootState) => state.auth.companies);
  const globalRoles = useSelector((state: RootState) => state.auth.globalRoles);

  const companyOptions: CompanyData[] = companies.length > 0 ? companies : [];
  const isGlobal = hasGlobalRole(globalRoles || []);
  const selectedCompanyId = getCurrentCompanyId() || '';

  const [rows, setRows] = useState<InventoryReportRow[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    company: 'all' as string,
  });

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company]
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company]
  );

  const { canCreate, resolveTabPermissions } = useModuleToolbarPermissions({
    resource: 'reports',
    activeCompanyFilter,
    updateTabTypes: ['update-report'],
  });

  const defaultCompanyForNewReport = useMemo(() => {
    if (toolbarTabCompanyFilter !== 'all') {
      return toolbarTabCompanyFilter;
    }
    return (
      selectedCompany?.company_id ||
      companyOptions[0]?.company_id ||
      selectedCompanyId ||
      ''
    );
  }, [companyOptions, selectedCompany?.company_id, selectedCompanyId, toolbarTabCompanyFilter]);

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      {
        id: 0,
        title: 'Danh sách báo cáo',
        type: 'list',
        closable: false,
        createdAt: Date.now(),
      },
    ];
    if (canCreate === true) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreate]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_reports_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: canCreate === true,
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-report', 'update-report'],
    allowMultipleTabTypes: ['new-report', 'update-report'],
    buildNewTab: (currentTabs) => {
      const companyId = defaultCompanyForNewReport || undefined;
      const formData = createDefaultReportForm(defaultCompanyForNewReport);
      return {
        id: Date.now(),
        title: `Report #${currentTabs.filter((tab) => tab.type === 'new-report').length + 1}`,
        type: 'new-report',
        closable: true,
        metadata: buildFormMetadata(formData, undefined, companyId),
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryReportFormData | undefined;
  const activeReportId = activeTabData?.metadata?.reportId as string | undefined;

  const toolbarCompanyId =
    filters.company === 'all' ? '' : filters.company;

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const matchesSearch =
          !filters.search ||
          `${row.code} ${row.name} ${row.reference} ${row.note}`
            .toLowerCase()
            .includes(filters.search.toLowerCase());
        const matchesStatus = filters.status === 'all' || row.status === filters.status;
        const matchesCompany =
          toolbarTabCompanyFilter === 'all' || row.company_id === toolbarTabCompanyFilter;
        return matchesSearch && matchesStatus && matchesCompany;
      }),
    [filters.search, filters.status, rows, toolbarTabCompanyFilter]
  );

  const updateFormData = useCallback(
    (updates: Partial<InventoryReportFormData>) => {
      if (!activeTabData?.id || !activeFormData) return;
      const nextFormData = { ...activeFormData, ...updates };
      updateTab(activeTabData.id, {
        metadata: buildFormMetadata(
          nextFormData,
          activeReportId,
          nextFormData.company_id
        ),
      });
      handleUnsavedChangesUpdate(true, activeTabData.id);
    },
    [activeFormData, activeReportId, activeTabData?.id, handleUnsavedChangesUpdate, updateTab]
  );

  const openExistingReport = useCallback(
    (row: InventoryReportRow) => {
      guardAndRun(() => {
        openTab({
          id: Date.now(),
          title: row.code,
          type: 'update-report',
          closable: true,
          entityId: row.id,
          metadata: buildFormMetadata(
            {
              report_type: row.report_type,
              code: row.code,
              name: row.name,
              reference: row.reference,
              qty: row.qty ? String(row.qty) : '',
              amount: row.amount ? String(row.amount) : '',
              status: row.status,
              company_id: row.company_id,
              note: row.note || '',
            },
            row.id,
            row.company_id
          ),
        });
      });
    },
    [guardAndRun, openTab]
  );

  const handleSave = useCallback(() => {
    if (!activeFormData || !activeTabData?.id) return;

    const tabPermissions = resolveTabPermissions(activeTabData);
    const isUpdateMode = Boolean(activeReportId);
    if (isUpdateMode && !tabPermissions.canUpdate) {
      setSnackbarMessage('Bạn không có quyền cập nhật báo cáo.');
      return;
    }
    if (!isUpdateMode && !tabPermissions.canCreate) {
      setSnackbarMessage('Bạn không có quyền tạo báo cáo.');
      return;
    }

    const company = companyOptions.find(
      (entry) => entry.company_id === activeFormData.company_id
    );
    const nextRow: InventoryReportRow = {
      id: activeReportId || `report-${Date.now()}`,
      report_type: activeFormData.report_type,
      code: activeFormData.code,
      name: activeFormData.name,
      reference: activeFormData.reference,
      qty: Number(activeFormData.qty || 0),
      amount: Number(activeFormData.amount || 0),
      status: activeFormData.status,
      company_id: activeFormData.company_id,
      company_name: company?.company_name || 'Chưa rõ công ty',
      note: activeFormData.note,
    };

    setRows((current) =>
      current.some((row) => row.id === nextRow.id)
        ? current.map((row) => (row.id === nextRow.id ? nextRow : row))
        : [nextRow, ...current]
    );

    handleUnsavedChangesUpdate(false, activeTabData.id);
    setSnackbarMessage(`Đã lưu ${nextRow.code || nextRow.name}.`);
    updateTab(activeTabData.id, {
      title: nextRow.code || nextRow.name,
      metadata: buildFormMetadata(activeFormData, nextRow.id, nextRow.company_id),
    });
  }, [
    activeFormData,
    activeReportId,
    activeTabData,
    companyOptions,
    handleUnsavedChangesUpdate,
    resolveTabPermissions,
    updateTab,
  ]);

  const handleToolbarCompanyChange = (companyId: string) => {
    setFilters((current) => ({
      ...current,
      company: !companyId ? 'all' : companyId,
    }));
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }

              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryReportFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box
                  key={tab.id}
                  sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}
                >
                  {tab.type === 'list' ? (
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
                    >
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <ToolbarListReport
                            searchQuery={filters.search}
                            status={filters.status}
                            companyId={toolbarCompanyId}
                            companyDisabled={!isGlobal}
                            companyOptions={companyOptions.map((company) => ({
                              company_id: company.company_id,
                              company_name: company.company_name,
                            }))}
                            statusOptions={reportStatusOptions}
                            onSearchChange={(query) =>
                              setFilters((current) => ({ ...current, search: query }))
                            }
                            onStatusChange={(status) =>
                              setFilters((current) => ({ ...current, status }))
                            }
                            onCompanyChange={handleToolbarCompanyChange}
                            onRefresh={() =>
                              setSnackbarMessage('Đã refresh danh sách báo cáo.')
                            }
                          />
                        }
                        dataTable={
                          <ReportList rows={filteredRows} onSelectRow={openExistingReport} />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <ReportForm
                        formData={tabFormData}
                        companyDisabled={!isGlobal}
                        companyOptions={companyOptions.map((company) => ({
                          company_id: company.company_id,
                          company_name: company.company_name,
                        }))}
                        statusOptions={reportStatusOptions}
                        reportTypeOptions={reportTypeOptions}
                        onChangeForm={(updates) => {
                          if (tab.id !== activeTabData?.id) {
                            const nextForm = { ...tabFormData, ...updates };
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                nextForm,
                                tab.metadata?.reportId as string | undefined,
                                nextForm.company_id
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={handleSave}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3500}
        onClose={() => setSnackbarMessage(null)}
      >
        <Alert severity='success' onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
